<template>
  <section id="asuo-rfi-section" class="container-fluid px-0">
    <div class="row gx-0">
      <!-- top img in mobile screen -->
      <div class="top-img rds-lazy ms-rfi-section" :data-background-image="bgImage"></div>
      <!-- in vw 992px or larger -->
      <div
        class="col-12 col-lg-8 rds-lazy ms-rfi-section ms-rfi-section"
        :data-background-image="bgImage"
      >
        <div class="col-12 col-lg-9 px-0 py-0 p-lg-space-lg my-lg-space-lg ml-70">
          <div class="bg-white rfi-box-shadow">
            <!-- rfi column -->
            <div>
              <form id="asuo-rfi" data-cy="rfi-form" @submit.prevent="handleSubmit">
                <!-- step-1: select degree -->
                <div
                  class="py-space-sm px-space-xs py-md-space-lg px-md-space-sm py-lg-space-xl px-lg-space-lg"
                >
                  <p class="fs-small mb-space-xs fw-bold">Step {{ currentStep }} of 2</p>

                  <h2 class="h2-medium mb-space-xs">{{ title }}</h2>

                  <p class="mb-space-xs text-dark-1 fs-small">
                    To learn more about ASU Online or a specific program, fill out the form below
                    and check your email for information on next steps.
                  </p>

                  <p class="mb-space-sm text-dark-1 fs-xs">* Indicates a required field</p>

                  <div id="form-fields" class="mb-lg-space-md">
                    <div v-show="currentStep === 1" id="step-1-form-fields">
                      <!-- degree type -->
                      <div id="degree-type-group" class="fw-bold mb-space-sm">
                        <label
                          id="degree-type-label"
                          for="degree-type_typeahead__search"
                          class="mb-space-xxs"
                        >
                          <span aria-hidden="true">*</span> Degree type
                        </label>

                        <TypeAheadSelect
                          id="degree-type"
                          v-model="form.degreeType as OptionType"
                          label-id="degree-type-label"
                          :options="degreeTypeOptions"
                          placeholder="Select a degree type"
                          preselect-opacity="1"
                          data-cy="rfi-degree-type-filter"
                          class="fw-normal text-dark-1"
                          @changed="handleDegreeTypeChange"
                        >
                        </TypeAheadSelect>
                      </div>

                      <!-- interest areas -->
                      <div id="interest-area-group" class="fw-bold mb-space-sm">
                        <label
                          id="interest-area-label"
                          for="interest-area_typeahead__search"
                          class="mb-space-xxs fs-small"
                          >Area of interest</label
                        >

                        <TypeAheadSelect
                          id="interest-area"
                          v-model="form.interestArea as OptionType"
                          :options="interestAreaOptions"
                          :disabled="interestAreaDisabled"
                          placeholder="Select an interest area"
                          preselect-opacity="1"
                          data-cy="rfi-interest-area-filter"
                          class="fw-normal text-dark-1"
                          label-id="interest-area-label"
                          @changed="handleInterestAreaChange"
                        >
                        </TypeAheadSelect>
                      </div>

                      <!-- programs -->
                      <div id="programs-group" class="fw-bold position-relative mb-0 pb-space-sm">
                        <label
                          id="program-label"
                          for="program_typeahead__search"
                          class="mb-space-xxs fs-small"
                        >
                          <span v-if="programIsRequired" aria-hidden="true">* </span>Program
                        </label>

                        <TypeAheadSelect
                          id="program"
                          v-model="form.program as OptionType"
                          label-id="program-label"
                          :options="programOptions"
                          :disabled="programDisabled"
                          placeholder="Select a program"
                          preselect-opacity="1"
                          data-cy="rfi-program-filter"
                          class="fw-normal text-dark-1"
                          @changed="handleProgramChange"
                        >
                        </TypeAheadSelect>
                      </div>

                      <!-- military radio group -->

                      <div
                        id="military-group"
                        role="radiogroup"
                        aria-labelledby="military-group-label"
                        class="form-check ps-0 position-relative mb-0 pb-space-sm d-flex flex-wrap"
                      >
                        <span
                          id="military-group-label"
                          class="fw-bold form-check-label fs-small w-100"
                        >
                          * Have you served in the U.S. military or are you a military dependent?
                        </span>
                        <div
                          v-for="option in militaryOptions"
                          :key="option.label"
                          class="form-check"
                        >
                          <label class="form-check-label fs-medium">
                            <input
                              v-model="form.isMilitary"
                              name="military"
                              class="form-check-input"
                              type="radio"
                              :value="option.value"
                              @change="handleMilitaryClick()"
                            />
                            {{ option.label }}
                          </label>
                        </div>
                      </div>
                    </div>

                    <div v-show="currentStep === 2" id="step-2-form-fields">
                      <!-- first-name input field -->
                      <div
                        id="first-name-group"
                        class="position-relative mb-0 pb-space-sm d-flex flex-column"
                      >
                        <label
                          id="homepage-rfi-first-name-label"
                          for="first-name"
                          class="fw-bold mb-space-xxs fs-small"
                          ><span aria-hidden="true">*</span> First name</label
                        >
                        <input
                          id="first-name"
                          ref="firstNameRef"
                          v-model.trim="form.firstName"
                          type="text"
                          aria-labelledby="homepage-rfi-first-name-label"
                          placeholder="Enter your first name"
                          data-cy="rfi-first-name"
                          class="form-control rounded-0"
                          :class="{
                            'is-invalid': firstNameErrorMessage
                          }"
                          :aria-invalid="firstNameErrorMessage ? 'true' : undefined"
                          :aria-describedby="
                            firstNameErrorMessage ? 'homepage-rfi-first-name-error' : undefined
                          "
                          :aria-errormessage="
                            firstNameErrorMessage ? 'homepage-rfi-first-name-error' : undefined
                          "
                          @blur="handleFirstNameFieldBlur"
                        />
                        <div
                          v-if="firstNameErrorMessage"
                          id="homepage-rfi-first-name-error"
                          class="invalid-feedback mb-space-xxs"
                          role="alert"
                          aria-live="assertive"
                          aria-atomic="true"
                          tabindex="0"
                        >
                          {{ firstNameErrorMessage }}
                        </div>
                      </div>

                      <!-- last-name input field -->
                      <div id="last-name-group" class="position-relative mb-0 pb-space-sm">
                        <label
                          id="homepage-rfi-last-name-label"
                          for="last-name"
                          class="fw-bold mb-space-xxs fs-small"
                          ><span aria-hidden="true">*</span> Last name</label
                        >
                        <input
                          id="last-name"
                          v-model.trim="form.lastName"
                          type="text"
                          aria-labelledby="homepage-rfi-last-name-label"
                          placeholder="Enter your last name"
                          data-cy="rfi-last-name"
                          class="form-control rounded-0"
                          :class="{
                            'is-invalid': lastNameErrorMessage
                          }"
                          :aria-invalid="lastNameErrorMessage ? 'true' : undefined"
                          :aria-describedby="
                            lastNameErrorMessage ? 'homepage-rfi-last-name-error' : undefined
                          "
                          :aria-errormessage="
                            lastNameErrorMessage ? 'homepage-rfi-last-name-error' : undefined
                          "
                          @blur="handleLastNameFieldBlur"
                        />
                        <div
                          v-if="lastNameErrorMessage"
                          id="homepage-rfi-last-name-error"
                          class="invalid-feedback mb-space-xxs"
                          role="alert"
                          aria-live="assertive"
                          aria-atomic="true"
                          tabindex="0"
                        >
                          {{ lastNameErrorMessage }}
                        </div>
                      </div>

                      <!-- email input field -->
                      <div id="email-group" class="position-relative mb-0 pb-space-sm">
                        <label
                          id="homepage-rfi-email-label"
                          for="email"
                          class="fw-bold mb-space-xxs fs-small"
                          ><span aria-hidden="true">*</span> Email</label
                        >
                        <input
                          id="email"
                          v-model.trim="form.email"
                          type="email"
                          aria-labelledby="homepage-rfi-email-label"
                          placeholder="Enter your email address"
                          class="form-control rounded-0"
                          :class="{
                            'is-invalid': emailErrorMessage
                          }"
                          :aria-invalid="emailErrorMessage ? 'true' : undefined"
                          :aria-describedby="
                            emailErrorMessage ? 'homepage-rfi-email-error' : undefined
                          "
                          :aria-errormessage="
                            emailErrorMessage ? 'homepage-rfi-email-error' : undefined
                          "
                          data-cy="rfi-email"
                          @input="handleEmailInput()"
                          @blur="handleEmailFieldBlur"
                        />
                        <div
                          v-if="emailErrorMessage"
                          id="homepage-rfi-email-error"
                          class="invalid-feedback mb-space-xxs"
                          role="alert"
                          aria-live="assertive"
                          aria-atomic="true"
                          tabindex="0"
                        >
                          {{ emailErrorMessage }}
                        </div>
                      </div>

                      <!-- phone number input field -->
                      <div
                        id="phone-number-group"
                        class="position-relative mb-0 pb-space-md fs-small"
                      >
                        <label for="asuonline_phone_number" class="fw-bold mb-space-xxs"
                          ><span aria-hidden="true">*</span> Phone number</label
                        >
                        <ClientOnly>
                          <PhoneInputRfi
                            id="asuonline_phone_number"
                            v-model.trim="form.phone.number"
                            class="w-100"
                            :class="{ 'is-invalid': displayedPhoneError }"
                            :error="displayedPhoneError != ''"
                            :no-validator-state="true"
                            :aria-invalid="displayedPhoneError ? 'true' : undefined"
                            :aria-describedby="
                              displayedPhoneError ? 'homepage-rfi-phone-number-error' : undefined
                            "
                            :aria-errormessage="
                              displayedPhoneError ? 'homepage-rfi-phone-number-error' : undefined
                            "
                            data-cy="rfi-phone-number"
                            @update="handlePhoneUpdate"
                            @blur="handlePhoneFieldBlur"
                          ></PhoneInputRfi>
                        </ClientOnly>
                        <div
                          v-if="displayedPhoneError"
                          id="homepage-rfi-phone-number-error"
                          ref="phoneErrorRef"
                          class="invalid-feedback mb-space-xxs"
                          role="alert"
                          aria-live="assertive"
                          aria-atomic="true"
                          tabindex="0"
                        >
                          {{ displayedPhoneError }}
                        </div>
                      </div>

                      <!-- consent block -->
                      <div class="fs-xs mb-space-sm mb-md-space-md gdpr-links">
                        <text-gdpr text-class="xs"></text-gdpr>
                      </div>
                      <input id="cq_req_id" type="hidden" name="cq_req_id" />
                      <input id="cq_req_ip" type="hidden" name="cq_req_ip" />
                    </div>
                  </div>

                  <!-- submit block -->
                  <div class="row gx-0">
                    <div class="col-12">
                      <div class="d-flex align-items-center" :class="submitContainerClass">
                        <button
                          v-show="currentStep === 2"
                          type="button"
                          aria-label="go to previous step"
                          class="text-dark-3 btn p-0"
                          data-cy="rfi-previous-step-button"
                          @click="previousStep"
                        >
                          <font-awesome-icon
                            icon="arrow-left"
                            size="2x"
                            alt="back icon"
                          ></font-awesome-icon>
                        </button>
                        <button
                          v-show="currentStep === 1"
                          :aria-disabled="!stepOneIsValid"
                          type="button"
                          class="fw-bold py-space-xs px-space-md btn btn-primary"
                          :class="{ disabled: !stepOneIsValid }"
                          data-cy="rfi-continue-button"
                          @click="nextStep"
                        >
                          Continue
                        </button>
                        <button
                          v-show="currentStep === 2"
                          class="fw-bold py-space-xs px-space-md btn btn-primary"
                          type="submit"
                          variant="primary"
                          :disabled="submitDisabled"
                          data-cy="rfi-submit-button"
                        >
                          Submit
                          <div v-if="showLoader" class="d-inline">
                            <span
                              class="spinner-grow spinner-grow-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            <span class="sr-only">Loading...</span>
                            <span
                              class="spinner-grow spinner-grow-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            <span class="sr-only">Loading...</span>
                            <span
                              class="spinner-grow spinner-grow-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            <span class="sr-only">Loading...</span>
                          </div>
                        </button>
                        <div
                          v-if="submissionError"
                          class="invalid-feedback d-block mt-space-xxs"
                          role="alert"
                          aria-live="assertive"
                          aria-atomic="true"
                          tabindex="0"
                        >
                          {{ submissionError }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";

import defaultBgImage from "@/assets/img/rfi-bg-overlay.png?url";
import PhoneInputRfi from "@/components/rfi/PhoneInputRfi.vue";
import TextGdpr from "@/components/rfi/TextGdpr.vue";
import type { OptionType } from "../TypeAheadSelect.vue";
import TypeAheadSelect from "../TypeAheadSelect.vue";

interface Props {
  title?: string;
  bgImage?: string;
}

withDefaults(defineProps<Props>(), {
  title: "Get started today",
  bgImage: defaultBgImage
});

const currentStep = ref(1);
const firstNameRef = ref<HTMLInputElement | null>(null);

const {
  form,
  lastNameErrorMessage,
  emailErrorMessage,
  phoneApiError,
  submissionError,
  firstNameErrorMessage,
  stepOneIsValid,
  programIsRequired,
  showLoader,
  submitDisabled,
  currentProgramCode,
  currentProgramName,
  degreeTypeOptions,
  interestAreaDisabled,
  programDisabled,
  handleDegreeTypeChange,
  programOptions,
  interestAreaOptions,
  handleEmailBlur: trackEmailBlur,
  handleEmailInput,
  handleFirstNameBlur: trackFirstNameBlur,
  handleInterestAreaChange,
  handleLastNameBlur: trackLastNameBlur,
  handleMilitaryClick,
  handlePhoneBlur: trackPhoneBlur,
  handlePhoneUpdate: updatePhoneState,
  handleProgramChange,
  syncResolvedStepOneSelection,
  handleSubmit
} = useRfi({
  enableUndecidedFlow: true
});

const phoneTouched = ref(false);
const phoneBlurred = ref(false);
const phoneErrorRef = ref<HTMLElement | null>(null);

const displayedPhoneError = computed(() => {
  if (phoneApiError.value) {
    return phoneApiError.value;
  }

  if (phoneBlurred.value && !form.value.phone.number) {
    return "Please enter a valid phone number and try again.";
  }

  if (phoneTouched.value && form.value.phone.number && form.value.phone.isValid === false) {
    return "Please enter a valid phone number and try again.";
  }

  return "";
});

const militaryOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false }
];

const submitContainerClass = computed(() => {
  return currentStep.value === 1 ? "justify-content-end" : "justify-content-between";
});

const previousStep = () => {
  currentStep.value = currentStep.value - 1;
  analyticsComposable.trackLinkEvent("internal link", "main content", "request info", "back arrow");
};

const handleFirstNameFieldBlur = () => {
  form.value.firstName = form.value.firstName ?? "";
  trackFirstNameBlur();
};

const handleLastNameFieldBlur = () => {
  form.value.lastName = form.value.lastName ?? "";
  trackLastNameBlur();
};

const handleEmailFieldBlur = () => {
  form.value.email = form.value.email ?? "";
  trackEmailBlur();
};

const handlePhoneFieldBlur = () => {
  phoneTouched.value = true;
  phoneBlurred.value = true;
  trackPhoneBlur();
  window.setTimeout(() => {
    if (displayedPhoneError.value) {
      phoneErrorRef.value?.focus();
    }
  }, 0);
};

const handlePhoneUpdate = (payload: Parameters<typeof updatePhoneState>[0]) => {
  phoneTouched.value = phoneTouched.value || Boolean(payload.nationalNumber);
  phoneBlurred.value = false;
  updatePhoneState(payload);
};

const nextStep = () => {
  if (!stepOneIsValid.value) {
    return;
  }

  syncResolvedStepOneSelection();

  if (currentProgramName.value && currentProgramCode.value) {
    analyticsComposable.trackEvent({
      event: "form",
      name: "onclick",
      action: "click",
      type: "click",
      region: "main content",
      section: "request info",
      text: "continue",
      degree_type:
        form.value.degreeType && form.value.degreeType.value
          ? form.value.degreeType.value.toLowerCase()
          : "select a degree type",
      area_of_interest:
        form.value.interestArea && form.value.interestArea.value
          ? form.value.interestArea.value.toLowerCase()
          : "select an interest area",
      program: currentProgramName.value.toLowerCase(),
      program_code: currentProgramCode.value,
      military_service: form.value.isMilitary === false ? "no" : "yes"
    });
    currentStep.value = currentStep.value + 1;
    nextTick(() => {
      firstNameRef.value?.focus();
    });
  } else {
    console.error("missing program name or code, when proceeding to next step");
  }
};
</script>

<style lang="scss" scoped>
.form-check-input:checked {
  color: var(--rds-dark-3);
  --rds-form-check-bg-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23000' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/%3e%3c/svg%3e");
  --rds-form-check-bg: var(--rds-secondary);
  background-color: var(--rds-secondary);
  border-color: var(--rds-secondary);
}
.rfi-box-shadow {
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.05);
}
.form-check {
  gap: 1rem;
}
.gdpr-links {
  :deep(a) {
    text-decoration: underline;
  }
}
.ms-rfi-section {
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;

  @media screen and (min-width: 992px) {
    background-position: center top;
  }
}
.top-img {
  min-height: 264px;

  @media screen and (min-width: 425px) {
    min-height: 300px;
  }
  @media screen and (min-width: 992px) {
    display: none;
  }
}

div.invalid-feedback {
  margin-top: 0 !important;
}

/* Medium devices (tablets, 768px and up) */
@media (min-width: 768px) {
}

/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  .ml-70 {
    margin-left: 70%;
  }
}
</style>
